import React, { useState, useEffect } from 'react';
import { AreaChart, Area, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { useClient } from '../../context/ClientContext';

export default function DailyCostTrend({ dateRange }) {
  const { brand, fetcher } = useClient();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetcher.fetchDailyTrend(dateRange)
      .then(setData)
      .finally(() => setLoading(false));
  }, [dateRange]);

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 animate-pulse">
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
        <div className="h-48 bg-gray-200 rounded"></div>
      </div>
    );
  }

  if (!data?.days?.length) return null;

  const primaryColor = brand.colors?.primary || '#2563eb';
  const currency = data.currency || '$';

  const formatted = data.days.map(d => ({
    ...d,
    label: formatDate(d.date),
    cost: Math.round(d.cost * 100) / 100
  }));

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 p-6">
      <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1"
          style={{ fontFamily: brand.typography?.headerFont }}>
        Daily Spend & Conversions
      </h3>
      <p className="text-xs text-gray-400 mb-4">Selected period</p>

      <ResponsiveContainer width="100%" height={250}>
        <AreaChart data={formatted} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
          <defs>
            <linearGradient id="costGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={primaryColor} stopOpacity={0.15} />
              <stop offset="95%" stopColor={primaryColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: '#666' }}
            tickLine={false}
            axisLine={{ stroke: '#e5e7eb' }}
            interval="preserveStartEnd"
          />
          <YAxis
            yAxisId="cost"
            tick={{ fontSize: 11, fill: '#666' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `${currency}${v}`}
          />
          <YAxis
            yAxisId="conv"
            orientation="right"
            tick={{ fontSize: 11, fill: '#666' }}
            tickLine={false}
            axisLine={false}
            allowDecimals={false}
          />
          <Tooltip
            formatter={(value, name) => {
              if (name === 'cost') return [`${currency}${value.toFixed(2)}`, 'Spend'];
              if (name === 'conversions') return [value, 'Conversions'];
              return [value, name];
            }}
            labelStyle={{ fontWeight: 600 }}
            contentStyle={{ borderRadius: 8, border: '1px solid #e5e7eb' }}
          />
          <Area
            yAxisId="cost"
            type="monotone"
            dataKey="cost"
            stroke={primaryColor}
            fill="url(#costGradient)"
            strokeWidth={2}
          />
          <Line
            yAxisId="conv"
            type="monotone"
            dataKey="conversions"
            stroke="#10b981"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        </AreaChart>
      </ResponsiveContainer>

      <div className="flex gap-4 mt-2 text-xs text-gray-500 justify-center">
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 rounded" style={{ backgroundColor: primaryColor }}></span>
          Spend
        </span>
        <span className="flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 rounded bg-emerald-500"></span>
          Conversions
        </span>
      </div>
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}
